/**
 * @file: index.tsx
 *
 * `ChatRoomDetailScreen` — the navigator binding for room detail: it mounts
 * `ChatRoomDetailView` and wires "add members" to push the contact picker on
 * the inbox stack. For an order-internal room it also renders the shared
 * `OrderLinkButton` in the mobile nav header (the view resolves the target and
 * reports it via `onOrderLink`). The view itself (re-exported here) is
 * navigation-agnostic and lives in `./components`.
 */

import type { AppNavParams } from '@euler/app/Routes';
import { ChatConversationHandle } from '@euler/app/flows/chat/lib/conversation/handle';
import { useFloatingPanelClose } from '@euler/app/flows/chat/lib/floating/panelContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useEffect, useState } from 'react';
import { ChatRoomDetailView } from './components/ChatRoomDetailView';
import { OrderLinkButton } from './components/OrderLinkButton';
import { OrderLinkTarget } from './functions/useRoomOrder';

export { ChatRoomDetailView };

export const ChatRoomDetailScreen = wrapNavigatorScreen(
  (handle: ChatConversationHandle) => {
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    // Inside the floating panel's standalone stack the conversation IS the
    // root, so closing the panel is the right exit; in the app-level chat flow
    // the conversation is the root too, so the exit is popping the WHOLE flow
    // off the root stack — popToTop would land back in the conversation of the
    // room that was just left/dismissed.
    const panelClose = useFloatingPanelClose();
    const [orderLink, setOrderLink] = useState<OrderLinkTarget | null>(null);

    useEffect(() => {
      navigation.setOptions({
        headerRight: orderLink
          ? () => <OrderLinkButton target={orderLink} />
          : undefined,
      });
      return () => navigation.setOptions({ headerRight: undefined });
    }, [navigation, orderLink]);

    return (
      <ChatRoomDetailView
        handle={handle}
        onAddMembers={p => navigation.navigate('_chatContactPicker' as any, p)}
        // After leaving/dismissing, the room (or membership) is gone: close the
        // floating panel, or dismiss the chat flow back to wherever it opened
        // from (the inbox list).
        onExit={panelClose ?? (() => navigation.getParent()?.goBack())}
        // After self-joining, open the conversation ("show the room"),
        // replacing the detail so back/close behaves like a normal open.
        onJoined={() =>
          navigation.replace('_chatConversation' as any, {
            kind: 'resolved',
            handle,
          })
        }
        onOrderLink={setOrderLink}
      />
    );
  },
  { title: '聊天详情' },
);
